import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  isConnected: boolean;
  status: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  sendMessage: (data: any) => void;
  subscribe: (channel: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10; // Daha uzun süre denemeye devam etmesi için artırıldı

  const connectRef = useRef<() => void>();

  const connect = useCallback(() => {
    if (!user) {return;}

    // Production'da Cloudflare Worker URL'ini, local'de 8888 portunu kullanır
    const host = import.meta.env.VITE_WS_URL || (import.meta.env.VITE_API_URL?.replace(/^http/, 'ws') || 'ws://localhost:8888');
    const wsUrl = `${host}/ws?user_id=${user.id}`;

    console.log(`[WS] Attempting connection to ${wsUrl}`);
    setStatus(prev => prev === 'disconnected' ? 'connecting' : 'reconnecting');
    
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('%c[WS] Connected Successfully', 'color: #10b981; font-weight: bold');
      setStatus('connected');
      reconnectAttempts.current = 0;
    };

    ws.current.onclose = (event) => {
      console.log(`%c[WS] Connection Closed: ${event.reason || 'No reason'}`, 'color: #ef4444');
      setStatus('disconnected');
      
      // Gemini Logic: Otomatik Retry (Exponential Backoff)
      if (reconnectAttempts.current < maxReconnectAttempts) {
        // İlk deneme 1sn, sonra 2sn, 4sn... Max 30sn
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(`[WS] Reconnect attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts} in ${delay}ms...`);
        
        reconnectTimeout.current = setTimeout(() => {
          reconnectAttempts.current += 1;
          if (connectRef.current) { connectRef.current(); }
        }, delay);
      } else {
        console.error('[WS] Maximum reconnect attempts reached.');
      }
    };

    ws.current.onerror = (error) => {
      console.error('[WS] Socket Error:', error);
      ws.current?.close();
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string);
        // Global yayınları burada dinleyebiliriz (Örn: Bildirimler)
        console.log('[WS] Received:', data);
      } catch (e) {
        console.error('[WS] Message parse error:', e);
      }
    };
  }, [user]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (user) {
      connect();
    }
    return () => {
      if (reconnectTimeout.current) {clearTimeout(reconnectTimeout.current);}
      if (ws.current) {ws.current.close();}
    };
  }, [user, connect]);

  const sendMessage = useCallback((data: any) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
    } else {
      console.warn('[WS] Socket is not open. Current state:', ws.current?.readyState);
    }
  }, []);

  const subscribe = useCallback((channel: string) => {
    sendMessage({ type: 'subscribe', channel });
  }, [sendMessage]);

  return (
    <WebSocketContext.Provider value={{ isConnected: status === 'connected', status, sendMessage, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};